import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { cn } from "@/lib/utils"

const Popover = (props: React.ComponentProps<typeof PopoverPrimitive.Root>) => {
  const { onOpenChange } = props;
  
  const handleOpenChange = React.useCallback((open: boolean) => {
    if (open) {
      // When opening, blur the trigger to avoid aria-hidden conflicts
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement?.tagName === 'BUTTON') {
          activeElement.blur();
        }
      }, 0);
    }
    onOpenChange?.(open);
  }, [onOpenChange]);

  return <PopoverPrimitive.Root {...props} onOpenChange={handleOpenChange} />;
};
const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & { container?: HTMLElement | null }
>(({ className, align = "center", sideOffset = 4, container, ...props }, ref) => {
  // Ensure portal renders directly to body to avoid aria-hidden ancestors
  const portalContainer = React.useMemo(() => {
    // If no custom container, render to body
    if (!container) return document.body;
    
    // If custom container is provided, check if it or any ancestor has aria-hidden
    let element = container;
    while (element && element !== document.body) {
      if (element.getAttribute('aria-hidden') === 'true') {
        // If ancestor has aria-hidden, render to body instead
        console.warn('Popover: Avoiding aria-hidden ancestor, rendering to body');
        return document.body;
      }
      element = element.parentElement as HTMLElement;
    }
    
    return container;
  }, [container]);

  return (
    <PopoverPrimitive.Portal container={portalContainer}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-[9999] w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        // Prevent focus issues with aria-hidden ancestors
        onOpenAutoFocus={(e) => {
          // Ensure the popover content is not inside an aria-hidden element
          const popoverElement = e.currentTarget as HTMLElement;
          let parent = popoverElement.parentElement;
          while (parent && parent !== document.body) {
            if (parent.getAttribute('aria-hidden') === 'true') {
              parent.removeAttribute('aria-hidden');
              parent.setAttribute('data-aria-hidden-removed', 'true');
            }
            parent = parent.parentElement;
          }
          
          // Call original onOpenAutoFocus if provided
          if (props.onOpenAutoFocus) {
            props.onOpenAutoFocus(e);
          }
        }}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent }